import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { usePageContext } from '../context/PageContext';

const ComprehensiveDashboard = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [timeRange, setTimeRange] = useState('month');
  const [hoveredData, setHoveredData] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [activeChart, setActiveChart] = useState(null);
  const { setPageTitle } = usePageContext();

  useEffect(() => {
    setPageTitle('Dashboard');
    fetchStats();
  }, [setPageTitle, selectedYear, selectedMonth, timeRange]);

  const fetchStats = async () => {
    try {
      const params = { year: selectedYear, month: selectedMonth, range: timeRange };
      const response = await axios.get('/api/dashboard/stats', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString()}`;

  const generateMonthlyData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, index) => ({
      month,
      revenue: (stats.monthlyRevenue?.[index] || 0),
      expenses: (stats.monthlyExpenses?.[index] || 0),
      profit: (stats.monthlyRevenue?.[index] || 0) - (stats.monthlyExpenses?.[index] || 0)
    }));
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  };

  const handleMouseMove = (e, data, chartType) => {
    const rect = e.currentTarget.closest('.card-body').getBoundingClientRect();
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setHoveredData(data);
    setActiveChart(chartType);
  };

  const handleMouseLeave = (chartType) => {
    if (activeChart === chartType) {
      setHoveredData(null);
      setActiveChart(null);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading Dashboard...</p>
      </div>
    );
  }

  const currentMonth = stats.currentMonth || {};
  const marginPercentage = currentMonth.marginPercentage || 0;
  const revenueGrowth = currentMonth.revenueGrowth || 0;
  const monthlyData = generateMonthlyData();
  const maxValue = Math.max(...monthlyData.map(d => Math.max(d.revenue, d.expenses)));

  return (
    <div>
      {/* Enhanced Time Period Selection */}
      <div className="card mb-3" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1" style={{ color: 'white' }}>📊 Business Intelligence Dashboard</h5>
              <p className="mb-0" style={{ opacity: 0.9, fontSize: '13px' }}>Real-time analytics and performance metrics</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0" style={{ color: 'white', fontSize: '13px' }}>View:</label>
                <select 
                  className="form-control" 
                  style={{ width: '120px', fontSize: '13px' }}
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <option value="month">Monthly</option>
                  <option value="quarter">Quarterly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0" style={{ color: 'white', fontSize: '13px' }}>Year:</label>
                <select 
                  className="form-control" 
                  style={{ width: '100px', fontSize: '13px' }}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                >
                  {getYearOptions().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              {timeRange === 'month' && (
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0" style={{ color: 'white', fontSize: '13px' }}>Month:</label>
                  <select 
                    className="form-control" 
                    style={{ width: '120px', fontSize: '13px' }}
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button className="btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }} onClick={fetchStats}>
                <i className="fas fa-sync-alt"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card" style={{ borderTop: '4px solid #007bff' }}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="stat-icon primary">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <div className="stat-value">{formatCurrency(currentMonth.totalRevenue)}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
            <div className="text-right">
              <div className={`stat-change ${revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
                <i className={`fas fa-arrow-${revenueGrowth >= 0 ? 'up' : 'down'}`}></i>
                {Math.abs(revenueGrowth)}%
              </div>
              <div style={{ fontSize: '11px', color: '#6c757d' }}>vs last period</div>
            </div>
          </div>
        </div>

        <div className="stat-card" style={{ borderTop: '4px solid #28a745' }}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="stat-icon success">
                <i className="fas fa-percentage"></i>
              </div>
              <div className="stat-value">{marginPercentage}%</div>
              <div className="stat-label">Profit Margin</div>
            </div>
            <div className="text-right">
              <div className="stat-change positive">
                <i className="fas fa-target"></i>
                Target: 25%
              </div>
              <div style={{ fontSize: '11px', color: marginPercentage >= 25 ? '#28a745' : '#dc3545' }}>
                {marginPercentage >= 25 ? 'On Target' : 'Below Target'}
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card" style={{ borderTop: '4px solid #dc3545' }}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="stat-icon warning">
                <i className="fas fa-receipt"></i>
              </div>
              <div className="stat-value">{formatCurrency(stats.totalExpenses)}</div>
              <div className="stat-label">Total Expenses</div>
            </div>
            <div className="text-right">
              <div className="stat-change negative">
                <i className="fas fa-clock"></i>
                {stats.pendingExpenses || 0}
              </div>
              <div style={{ fontSize: '11px', color: '#6c757d' }}>pending approval</div>
            </div>
          </div>
        </div>

        <div className="stat-card" style={{ borderTop: '4px solid #17a2b8' }}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="stat-icon info">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="stat-value">{formatCurrency(currentMonth.totalRevenue - (stats.totalExpenses || 0))}</div>
              <div className="stat-label">Net Profit</div>
            </div>
            <div className="text-right">
              <div className="stat-change positive">
                <i className="fas fa-coins"></i>
                ROI
              </div>
              <div style={{ fontSize: '11px', color: '#6c757d' }}>after expenses</div>
            </div>
          </div>
        </div>

        <div className="stat-card" style={{ borderTop: '4px solid #6f42c1' }}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #6f42c1, #5a2d91)', color: 'white' }}>
                <i className="fas fa-credit-card"></i>
              </div>
              <div className="stat-value">{formatCurrency(stats.totalDueAmount || 0)}</div>
              <div className="stat-label">Due Payments</div>
            </div>
            <div className="text-right">
              <div className="stat-change" style={{ color: '#6f42c1' }}>
                <i className="fas fa-dollar-sign"></i>
                {formatCurrency(stats.totalPaidAmount || 0)}
              </div>
              <div style={{ fontSize: '11px', color: '#6c757d' }}>total paid</div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Charts Row */}
      <div className="grid grid-cols-2 mb-4">
        {/* Interactive Revenue vs Expenses Chart */}
        <div className="card">
          <div className="card-header" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="card-title">
                <i className="fas fa-chart-area text-primary"></i>
                Revenue vs Expenses Trend
              </h3>
              <div className="d-flex gap-3">
                <div className="d-flex align-items-center gap-1">
                  <div style={{ width: '12px', height: '3px', background: '#007bff', borderRadius: '2px' }}></div>
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>Revenue</span>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <div style={{ width: '12px', height: '3px', background: '#dc3545', borderRadius: '2px' }}></div>
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>Expenses</span>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div style={{ height: '320px', position: 'relative' }} onMouseLeave={() => handleMouseLeave('revenue-expenses')}>
              <svg width="100%" height="100%" viewBox="0 0 450 280">
                {/* Enhanced Grid */}
                <defs>
                  <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#007bff" stopOpacity="0.3"/>
                    <stop offset="100%" stopColor="#007bff" stopOpacity="0.1"/>
                  </linearGradient>
                  <linearGradient id="expenseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#dc3545" stopOpacity="0.3"/>
                    <stop offset="100%" stopColor="#dc3545" stopOpacity="0.1"/>
                  </linearGradient>
                </defs>
                
                {/* Grid lines */}
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <g key={i}>
                    <line x1="50" y1={50 + i * 35} x2="420" y2={50 + i * 35} stroke="#f0f0f0" strokeWidth="1"/>
                    <text x="45" y={55 + i * 35} textAnchor="end" fontSize="10" fill="#999">
                      ${Math.round(maxValue * (5-i) / 5 / 1000)}K
                    </text>
                  </g>
                ))}
                
                {/* Revenue area */}
                <path
                  d={`M 70,${225 - (monthlyData[0]?.revenue / maxValue) * 175} ${monthlyData.slice(0, 12).map((d, i) => 
                    `L ${70 + i * 30},${225 - (d.revenue / maxValue) * 175}`
                  ).join(' ')} L ${70 + 11 * 30},225 L 70,225 Z`}
                  fill="url(#revenueGradient)"
                />
                
                {/* Expenses area */}
                <path
                  d={`M 70,${225 - (monthlyData[0]?.expenses / maxValue) * 175} ${monthlyData.slice(0, 12).map((d, i) => 
                    `L ${70 + i * 30},${225 - (d.expenses / maxValue) * 175}`
                  ).join(' ')} L ${70 + 11 * 30},225 L 70,225 Z`}
                  fill="url(#expenseGradient)"
                />
                
                {/* Revenue line */}
                <polyline
                  fill="none"
                  stroke="#007bff"
                  strokeWidth="3"
                  points={monthlyData.slice(0, 12).map((d, i) => 
                    `${70 + i * 30},${225 - (d.revenue / maxValue) * 175}`
                  ).join(' ')}
                />
                
                {/* Expenses line */}
                <polyline
                  fill="none"
                  stroke="#dc3545"
                  strokeWidth="3"
                  points={monthlyData.slice(0, 12).map((d, i) => 
                    `${70 + i * 30},${225 - (d.expenses / maxValue) * 175}`
                  ).join(' ')}
                />
                
                {/* Interactive data points */}
                {monthlyData.slice(0, 12).map((d, i) => (
                  <g key={i}>
                    <circle 
                      cx={70 + i * 30} 
                      cy={225 - (d.revenue / maxValue) * 175} 
                      r="6" 
                      fill="#007bff"
                      stroke="white"
                      strokeWidth="2"
                      style={{ cursor: 'pointer' }}
                      onMouseMove={(e) => handleMouseMove(e, { month: d.month, value: d.revenue, type: 'Revenue', color: '#007bff' }, 'revenue-expenses')}
                    />
                    <circle 
                      cx={70 + i * 30} 
                      cy={225 - (d.expenses / maxValue) * 175} 
                      r="6" 
                      fill="#dc3545"
                      stroke="white"
                      strokeWidth="2"
                      style={{ cursor: 'pointer' }}
                      onMouseMove={(e) => handleMouseMove(e, { month: d.month, value: d.expenses, type: 'Expenses', color: '#dc3545' }, 'revenue-expenses')}
                    />
                  </g>
                ))}
                
                {/* X-axis labels */}
                {monthlyData.slice(0, 12).map((d, i) => (
                  <text key={i} x={70 + i * 30} y={250} textAnchor="middle" fontSize="11" fill="#666" fontWeight="500">
                    {d.month}
                  </text>
                ))}
              </svg>
              
              {/* Enhanced Tooltip */}
              {hoveredData && activeChart === 'revenue-expenses' && (
                <div 
                  style={{
                    position: 'absolute',
                    left: tooltipPos.x + 10,
                    top: tooltipPos.y - 10,
                    background: 'rgba(0,0,0,0.9)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>{hoveredData.month} {selectedYear}</div>
                  <div style={{ color: hoveredData.color }}>
                    {hoveredData.type}: {formatCurrency(hoveredData.value)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Profit Margin Chart */}
        <div className="card">
          <div className="card-header" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="card-title">
                <i className="fas fa-chart-bar text-success"></i>
                Monthly Profit Margin
              </h3>
              <span className="badge badge-success">Target: 25%</span>
            </div>
          </div>
          <div className="card-body">
            <div style={{ height: '320px', position: 'relative' }} onMouseLeave={() => handleMouseLeave('profit-margin')}>
              <svg width="100%" height="100%" viewBox="0 0 450 280">
                {/* Grid lines */}
                {[0, 10, 20, 30, 40, 50].map(i => (
                  <g key={i}>
                    <line x1="50" y1={225 - (i / 50) * 175} x2="420" y2={225 - (i / 50) * 175} stroke="#f0f0f0" strokeWidth="1"/>
                    <text x="45" y={230 - (i / 50) * 175} textAnchor="end" fontSize="10" fill="#999">
                      {i}%
                    </text>
                  </g>
                ))}
                
                {/* Enhanced Bars with gradients */}
                {monthlyData.slice(0, 12).map((d, i) => {
                  const profit = d.profit;
                  const profitMargin = d.revenue > 0 ? (profit / d.revenue) * 100 : 0;
                  const barHeight = (profitMargin / 50) * 175;
                  const barColor = profitMargin >= 25 ? "#28a745" : profitMargin >= 15 ? "#ffc107" : "#dc3545";
                  
                  return (
                    <g key={i}>
                      <defs>
                        <linearGradient id={`barGradient${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor={barColor} stopOpacity="0.9"/>
                          <stop offset="100%" stopColor={barColor} stopOpacity="0.7"/>
                        </linearGradient>
                      </defs>
                      <rect
                        x={60 + i * 30}
                        y={225 - barHeight}
                        width="24"
                        height={barHeight}
                        fill={`url(#barGradient${i})`}
                        stroke={barColor}
                        strokeWidth="1"
                        rx="2"
                        style={{ cursor: 'pointer' }}
                        onMouseMove={(e) => handleMouseMove(e, { 
                          month: d.month, 
                          margin: profitMargin.toFixed(1), 
                          profit: profit,
                          revenue: d.revenue,
                          type: 'Profit Margin' 
                        }, 'profit-margin')}
                      />
                      {/* Value labels on bars */}
                      {barHeight > 20 && (
                        <text 
                          x={72 + i * 30} 
                          y={220 - barHeight/2} 
                          textAnchor="middle" 
                          fontSize="9" 
                          fill="white" 
                          fontWeight="600"
                        >
                          {profitMargin.toFixed(0)}%
                        </text>
                      )}
                    </g>
                  );
                })}
                
                {/* Target line at 25% */}
                <line x1="50" y1={137.5} x2="420" y2={137.5} stroke="#28a745" strokeWidth="2" strokeDasharray="8,4"/>
                <text x="425" y="142" fontSize="10" fill="#28a745" fontWeight="600">Target</text>
                
                {/* X-axis labels */}
                {monthlyData.slice(0, 12).map((d, i) => (
                  <text key={i} x={72 + i * 30} y={250} textAnchor="middle" fontSize="11" fill="#666" fontWeight="500">
                    {d.month}
                  </text>
                ))}
              </svg>
              
              {/* Enhanced Tooltip */}
              {hoveredData && activeChart === 'profit-margin' && (
                <div 
                  style={{
                    position: 'absolute',
                    left: tooltipPos.x + 10,
                    top: tooltipPos.y - 10,
                    background: 'rgba(0,0,0,0.9)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>{hoveredData.month} {selectedYear}</div>
                  <div style={{ color: '#28a745' }}>Margin: {hoveredData.margin}%</div>
                  <div style={{ color: '#17a2b8' }}>Profit: {formatCurrency(hoveredData.profit)}</div>
                  <div style={{ color: '#007bff' }}>Revenue: {formatCurrency(hoveredData.revenue)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Expense Analytics Row */}
      <div className="grid grid-cols-3 mb-4">
        {/* Expense Breakdown with Donut Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-chart-pie text-warning"></i>
              Expense Categories
            </h3>
          </div>
          <div className="card-body">
            <div style={{ height: '200px', marginBottom: '1rem' }}>
              <svg width="100%" height="100%" viewBox="0 0 200 200">
                {/* Simple donut chart representation */}
                <circle cx="100" cy="100" r="60" fill="none" stroke="#e9ecef" strokeWidth="20"/>
                <circle cx="100" cy="100" r="60" fill="none" stroke="#007bff" strokeWidth="20" 
                        strokeDasharray="188" strokeDashoffset="47" transform="rotate(-90 100 100)"/>
              </svg>
            </div>
            {stats.expenseBreakdown?.map((expense, index) => (
              <div key={expense._id} className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                  <div 
                    style={{ 
                      width: '16px', 
                      height: '16px',
                      borderRadius: '50%',
                      background: ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6c757d'][index % 5]
                    }}
                  ></div>
                  <div>
                    <div className="font-medium">{expense._id || 'Other'}</div>
                    <div className="text-sm text-muted">{expense.count} expenses</div>
                  </div>
                </div>
                <div className="font-semibold">{formatCurrency(expense.total)}</div>
              </div>
            ))}
            {(!stats.expenseBreakdown || stats.expenseBreakdown.length === 0) && (
              <div className="empty-state">
                <div className="empty-state-icon">💰</div>
                <div className="empty-state-title">No Expense Data</div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Pending Expenses */}
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="card-title">
                <i className="fas fa-clock text-warning"></i>
                Pending Expenses
              </h3>
              <span className="badge badge-warning">{stats.pendingExpenses || 0}</span>
            </div>
          </div>
          <div className="card-body">
            {stats.pendingExpensesList?.map((expense) => (
              <div key={expense._id} className="card mb-2" style={{ border: '1px solid #ffc107', borderLeft: '4px solid #ffc107' }}>
                <div className="card-body" style={{ padding: '0.75rem' }}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="font-medium">{expense.recipient?.name || 'Unknown'}</div>
                      <div className="text-sm text-muted">{expense.description}</div>
                      <div className="text-sm text-muted">
                        <i className="fas fa-calendar-alt"></i> {new Date(expense.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-warning">{formatCurrency(expense.amount)}</div>
                      <span className="badge badge-warning">Pending</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(!stats.pendingExpensesList || stats.pendingExpensesList.length === 0) && (
              <div className="empty-state">
                <div className="empty-state-icon">✅</div>
                <div className="empty-state-title">No Pending Expenses</div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Expense Trends */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-trending-up text-info"></i>
              Expense Trends
            </h3>
          </div>
          <div className="card-body">
            <div style={{ height: '200px' }}>
              <svg width="100%" height="100%" viewBox="0 0 300 180">
                <defs>
                  <linearGradient id="expenseTrendGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#dc3545" stopOpacity="0.3"/>
                    <stop offset="100%" stopColor="#dc3545" stopOpacity="0.1"/>
                  </linearGradient>
                </defs>
                
                {/* Trend area */}
                <path
                  d={`M 30,${150 - (monthlyData[0]?.expenses / (maxValue || 1)) * 120} ${monthlyData.slice(0, 6).map((d, i) => 
                    `L ${30 + i * 45},${150 - (d.expenses / (maxValue || 1)) * 120}`
                  ).join(' ')} L ${30 + 5 * 45},150 L 30,150 Z`}
                  fill="url(#expenseTrendGradient)"
                />
                
                {/* Trend line */}
                <polyline
                  fill="none"
                  stroke="#dc3545"
                  strokeWidth="3"
                  points={monthlyData.slice(0, 6).map((d, i) => 
                    `${30 + i * 45},${150 - (d.expenses / (maxValue || 1)) * 120}`
                  ).join(' ')}
                />
                
                {/* Data points */}
                {monthlyData.slice(0, 6).map((d, i) => (
                  <circle key={i} cx={30 + i * 45} cy={150 - (d.expenses / (maxValue || 1)) * 120} r="4" fill="#dc3545" stroke="white" strokeWidth="2"/>
                ))}
                
                {/* Labels */}
                {monthlyData.slice(0, 6).map((d, i) => (
                  <text key={i} x={30 + i * 45} y={170} textAnchor="middle" fontSize="8" fill="#666">
                    {d.month}
                  </text>
                ))}
              </svg>
            </div>
            <div className="mt-3">
              <div className="d-flex justify-content-between text-sm mb-2">
                <span>Avg Monthly:</span>
                <span className="font-semibold">{formatCurrency(stats.avgMonthlyExpenses)}</span>
              </div>
              <div className="d-flex justify-content-between text-sm mb-2">
                <span>This Month:</span>
                <span className="font-semibold text-warning">{formatCurrency(stats.currentMonthExpenses)}</span>
              </div>
              <div className="progress-container" style={{ height: '8px' }}>
                <div 
                  className="progress-bar"
                  style={{ 
                    width: `${Math.min((stats.currentMonthExpenses / (stats.avgMonthlyExpenses || 1)) * 100, 100)}%`,
                    background: stats.currentMonthExpenses > stats.avgMonthlyExpenses ? '#dc3545' : '#28a745'
                  }}
                ></div>
              </div>
              <div className="text-center mt-1">
                <span style={{ fontSize: '11px', color: stats.currentMonthExpenses > stats.avgMonthlyExpenses ? '#dc3545' : '#28a745' }}>
                  {stats.currentMonthExpenses > stats.avgMonthlyExpenses ? 'Above Average' : 'Below Average'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row - Performance & Activity */}
      <div className="grid grid-cols-2 mb-4">
        {/* Enhanced Top Performers */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-trophy text-warning"></i>
              Top Performers
            </h3>
          </div>
          <div className="card-body">
            {stats.topPerformers?.length > 0 ? (
              stats.topPerformers.map((performer, index) => (
                <div key={performer._id} className="card mb-2" style={{ border: '1px solid #e9ecef', borderLeft: `4px solid ${index === 0 ? '#ffc107' : index === 1 ? '#6c757d' : '#dc3545'}` }}>
                  <div className="card-body" style={{ padding: '1rem' }}>
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        className="d-flex align-items-center justify-content-center font-bold"
                        style={{ 
                          width: '50px', 
                          height: '50px',
                          borderRadius: '50%',
                          background: index === 0 ? 'linear-gradient(135deg, #ffc107, #ff8c00)' : 
                                     index === 1 ? 'linear-gradient(135deg, #6c757d, #495057)' : 
                                     'linear-gradient(135deg, #dc3545, #c82333)',
                          color: 'white',
                          fontSize: '18px',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                        }}
                      >
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                      </div>
                      <div className="w-100">
                        <div className="font-semibold text-lg">{performer.name}</div>
                        <div className="d-flex justify-content-between text-sm mb-2">
                          <span className="text-muted">
                            <i className="fas fa-clock"></i> {performer.totalHours} hours
                          </span>
                          <span className="text-success font-bold">
                            <i className="fas fa-dollar-sign"></i> {formatCurrency(performer.totalRevenue)}
                          </span>
                        </div>
                        <div className="progress-container" style={{ height: '8px' }}>
                          <div 
                            className="progress-bar progress-success"
                            style={{ width: `${performer.marginPercentage}%` }}
                          ></div>
                        </div>
                        <div className="text-center mt-1">
                          <span style={{ fontSize: '11px', color: '#28a745', fontWeight: '600' }}>
                            {performer.marginPercentage.toFixed(1)}% margin
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📈</div>
                <div className="empty-state-title">No Performance Data</div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-history text-info"></i>
              Recent Activity
            </h3>
          </div>
          <div className="card-body">
            {stats.recentInvoices?.length > 0 ? (
              stats.recentInvoices.map(invoice => (
                <div key={invoice._id} className="card mb-2" style={{ border: '1px solid #e9ecef' }}>
                  <div className="card-body" style={{ padding: '1rem' }}>
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        className="d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '50px', 
                          height: '50px',
                          borderRadius: '12px',
                          background: invoice.status === 'Paid' ? 'linear-gradient(135deg, #28a745, #20c997)' : 
                                     invoice.status === 'Sent' ? 'linear-gradient(135deg, #ffc107, #fd7e14)' : 
                                     'linear-gradient(135deg, #6c757d, #495057)',
                          color: 'white',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                        }}
                      >
                        <i className="fas fa-file-invoice"></i>
                      </div>
                      <div className="w-100">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="font-semibold">{invoice.employee?.name}</div>
                            <div className="text-sm text-muted">
                              <i className="fas fa-hashtag"></i> {invoice.invoiceNumber}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-primary">{formatCurrency(invoice.invoiceAmount)}</div>
                            <div className="text-sm text-success">
                              <i className="fas fa-plus"></i> {formatCurrency(invoice.caliberRevenue)}
                            </div>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span className={`badge badge-${
                            invoice.status === 'Paid' ? 'success' : 
                            invoice.status === 'Sent' ? 'warning' : 'secondary'
                          }`}>
                            <i className={`fas fa-${
                              invoice.status === 'Paid' ? 'check-circle' : 
                              invoice.status === 'Sent' ? 'paper-plane' : 'clock'
                            }`}></i> {invoice.status}
                          </span>
                          <span className="text-sm text-muted">
                            <i className="fas fa-calendar"></i> {new Date(invoice.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">No Recent Activity</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Quick Actions */}
      <div className="card">
        <div className="card-header" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
          <h3 className="card-title">
            <i className="fas fa-bolt text-primary"></i>
            Quick Actions
          </h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-4">
            <a href="/invoices" className="btn btn-primary btn-lg text-center" style={{ background: 'linear-gradient(135deg, #007bff, #0056b3)', border: 'none', boxShadow: '0 4px 8px rgba(0,123,255,0.3)' }}>
              <i className="fas fa-plus" style={{ fontSize: '24px', marginBottom: '0.5rem', display: 'block' }}></i>
              <div className="font-bold">Create Invoice</div>
              <div className="text-sm" style={{ opacity: 0.9, marginTop: '0.25rem' }}>Generate new invoice</div>
            </a>
            <a href="/expenses" className="btn btn-warning btn-lg text-center" style={{ background: 'linear-gradient(135deg, #ffc107, #e0a800)', border: 'none', boxShadow: '0 4px 8px rgba(255,193,7,0.3)' }}>
              <i className="fas fa-receipt" style={{ fontSize: '24px', marginBottom: '0.5rem', display: 'block' }}></i>
              <div className="font-bold">Add Expense</div>
              <div className="text-sm" style={{ opacity: 0.9, marginTop: '0.25rem' }}>Record new expense</div>
            </a>
            <a href="/employees" className="btn btn-success btn-lg text-center" style={{ background: 'linear-gradient(135deg, #28a745, #1e7e34)', border: 'none', boxShadow: '0 4px 8px rgba(40,167,69,0.3)' }}>
              <i className="fas fa-user-plus" style={{ fontSize: '24px', marginBottom: '0.5rem', display: 'block' }}></i>
              <div className="font-bold">Add Employee</div>
              <div className="text-sm" style={{ opacity: 0.9, marginTop: '0.25rem' }}>Onboard consultant</div>
            </a>
            <a href="/reports" className="btn btn-secondary btn-lg text-center" style={{ background: 'linear-gradient(135deg, #6c757d, #545b62)', border: 'none', boxShadow: '0 4px 8px rgba(108,117,125,0.3)' }}>
              <i className="fas fa-chart-bar" style={{ fontSize: '24px', marginBottom: '0.5rem', display: 'block' }}></i>
              <div className="font-bold">Reports</div>
              <div className="text-sm" style={{ opacity: 0.9, marginTop: '0.25rem' }}>View analytics</div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveDashboard;